/**
 * Download Manager for VerticalFree
 * Handles secure download functionality with the Download Broker API
 */

(function() {
  'use strict';

  // Configuration
  const DOWNLOAD_API_ACTIVE = Boolean(window.VERTICALY_DOWNLOAD_API_ENABLED);
  const DOWNLOAD_API_BASE = 'https://download-broker-343305593182.asia-northeast1.run.app/apps/api/download';
  const SUBSCRIPTION_API_BASE = 'https://download-broker-343305593182.asia-northeast1.run.app/apps/api/download/subscription';
  const DOWNLOAD_ENDPOINT = `${DOWNLOAD_API_BASE}`;
  const SUBSCRIPTION_ENDPOINT = `${SUBSCRIPTION_API_BASE}`;

  // State
  let downloadInProgress = false;
  let subscriptionStatus = null;

  /**
   * Initialize download manager
   */
  function init() {
    // Add event listeners to download buttons
    document.addEventListener('DOMContentLoaded', () => {
      attachDownloadListeners();
      if (DOWNLOAD_API_ACTIVE) {
        loadSubscriptionStatus();
      } else {
        showNotification('セキュリティ強化のためダウンロード機能を一時停止しています。', 'warning');
      }
    });
  }

  /**
   * Attach listeners to download buttons
   */
  function attachDownloadListeners() {
    const downloadButtons = document.querySelectorAll('[data-download-product]');

    downloadButtons.forEach(button => {
      button.addEventListener('click', handleDownloadClick);
    });
  }

  /**
   * Handle download button click
   */
  async function handleDownloadClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const productId = button.dataset.downloadProduct;
    const productTitle = button.dataset.productTitle || '動画';
    const orderId = button.dataset.orderId || null;

    // Prevent multiple simultaneous downloads
    if (downloadInProgress) {
      showNotification('ダウンロード処理中です。しばらくお待ちください。', 'info');
      return;
    }

    // Check if logged in
    if (!window.v0Customer?.loggedIn) {
      showNotification('ダウンロードにはログインが必要です', 'error');
      window.location.href = '/account/login';
      return;
    }

    if (!DOWNLOAD_API_ACTIVE) {
      showNotification('セキュリティ強化のためダウンロード機能を一時停止しています。', 'warning');
      return;
    }

    try {
      downloadInProgress = true;
      button.disabled = true;
      button.textContent = '準備中...';

      // Generate download token
      const tokenResponse = await generateDownloadToken(productId, orderId);

      if (!tokenResponse.success) {
        const error = new Error(tokenResponse.message || 'トークン生成に失敗しました');
        error.code = tokenResponse.error; // Pass error code
        throw error;
      }

      // Start download — prefer signed URL, fallback to fetchUrl with absolute path
      button.textContent = 'ダウンロード中...';
      const downloadUrl = tokenResponse.download_url || tokenResponse.fetchUrl;
      await startDownload(downloadUrl, productTitle);

      // Success
      button.textContent = 'ダウンロード完了';
      showNotification(`${productTitle}のダウンロードが完了しました`, 'success');

      // Update subscription status if needed
      if (subscriptionStatus) {
        await loadSubscriptionStatus();
      }

      // Reset button after 3 seconds
      setTimeout(() => {
        button.textContent = 'ダウンロード済み';
        button.disabled = true;
      }, 3000);

    } catch (error) {
      console.error('Download error:', error);
      button.textContent = 'ダウンロード';
      button.disabled = false;

      // Handle specific errors by error code (primary) or message (fallback)
      const errorCode = error.code || '';
      const errorMessage = error.message || '';

      if (errorCode === 'ALREADY_DOWNLOADED' || errorMessage.includes('既にダウンロード')) {
        showNotification('この商品は既にダウンロード済みです', 'warning');
        button.textContent = 'ダウンロード済み';
        button.disabled = true;
      } else if (errorCode === 'UNAUTHORIZED' || errorCode === 'NOT_PURCHASED' || errorMessage.includes('購入されていません')) {
        showNotification('この商品を購入してからダウンロードしてください', 'error');
      } else if (errorCode === 'NO_CREDITS' || errorCode === 'INSUFFICIENT_CREDITS' || errorMessage.includes('上限')) {
        showNotification('今月のダウンロード上限に達しました', 'warning');
      } else if (errorCode === 'FILE_NOT_FOUND' || errorCode === 'CONTENT_NOT_FOUND') {
        showNotification('ファイルが見つかりません。サポートにお問い合わせください。', 'error');
      } else if (errorCode === 'DOWNLOAD_API_DISABLED' || errorCode === 'DOWNLOAD_DISABLED') {
        showNotification('ダウンロード機能は現在利用できません', 'warning');
      } else {
        showNotification(errorMessage || 'ダウンロードに失敗しました', 'error');
      }
    } finally {
      downloadInProgress = false;
    }
  }

  /**
   * Generate download token
   */
  async function generateDownloadToken(productId, orderId) {
    if (!DOWNLOAD_API_ACTIVE) {
      throw new Error('DOWNLOAD_DISABLED');
    }

    // Get customer ID and name from Shopify global object
    const customerId = window.v0Customer?.id;
    const customerName = window.v0Customer?.name;
    const customerEmail = window.v0Customer?.email;

    if (!customerId) {
      throw new Error('Customer ID not found');
    }

    const requestBody = {
      product_id: productId,
      customer_id: customerId,
      customer_name: customerName,
      customer_email: customerEmail
    };
    if (orderId) {
      requestBody.order_id = orderId;
    }

    const response = await fetch(`${DOWNLOAD_ENDPOINT}/generate`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Customer-Id': customerId
      },
      body: JSON.stringify(requestBody)
    });

    // Check HTTP status code before parsing JSON
    if (!response.ok) {
      // Try to parse error response
      try {
        const errorData = await response.json();
        const error = new Error(errorData.message || `HTTP Error ${response.status}`);
        error.code = errorData.error || `HTTP_${response.status}`;
        throw error;
      } catch (parseError) {
        // If JSON parsing fails, throw generic error
        if (parseError.code) throw parseError; // Re-throw if it's our error
        const error = new Error(`サーバーエラー (${response.status})`);
        error.code = `HTTP_${response.status}`;
        throw error;
      }
    }

    return await response.json();
  }

  /**
   * Start file download
   */
  async function startDownload(fetchUrl, filename) {
    // Ensure absolute URL (fetchUrl from API may be relative path)
    let downloadUrl = fetchUrl;
    if (fetchUrl && fetchUrl.startsWith('/')) {
      downloadUrl = `https://download-broker-343305593182.asia-northeast1.run.app${fetchUrl}`;
    }

    // Use fetch + Blob for proper filename handling
    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error('ダウンロードに失敗しました');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename + '.mp4';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  /**
   * Load subscription status
   */
  async function loadSubscriptionStatus() {
    if (!DOWNLOAD_API_ACTIVE || !window.v0Customer?.loggedIn) {
      return;
    }

    try {
      // Get customer ID from Shopify global object
      const customerId = window.v0Customer?.id;
      if (!customerId) {
        console.error('Customer ID not found');
        return;
      }

      const response = await fetch(`${SUBSCRIPTION_ENDPOINT}/status`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Customer-Id': customerId
        },
        body: JSON.stringify({
          customer_id: customerId
        })
      });

      const data = await response.json();

      if (data.success && data.has_subscription) {
        subscriptionStatus = data;
        updateSubscriptionDisplay(data);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    }
  }

  /**
   * Update subscription display
   */
  function updateSubscriptionDisplay(status) {
    // Update download count display
    const countElement = document.getElementById('download-count');
    if (countElement) {
      countElement.textContent = status.downloads_used;
    }

    // Update remaining downloads
    const remainingElement = document.getElementById('downloads-remaining');
    if (remainingElement) {
      remainingElement.textContent = `${status.downloads_remaining}/${status.downloads_limit}`;
    }

    // Update progress bar
    const progressBar = document.querySelector('.subscription-status__progress-bar');
    if (progressBar) {
      const percentage = ((status.downloads_limit - status.downloads_used) / status.downloads_limit) * 100;
      progressBar.style.width = `${percentage}%`;
    }

    // Update reset date
    const resetElement = document.querySelector('.subscription-status__reset');
    if (resetElement) {
      resetElement.textContent = `次回リセット: ${status.reset_date}`;
    }
  }

  /**
   * Show notification
   */
  function showNotification(message, type = 'info') {
    const existing = document.querySelector('.download-notification');
    if (existing) {
      existing.remove();
    }

    const notification = document.createElement('div');
    notification.className = `download-notification download-notification--${type}`;

    const content = document.createElement('div');
    content.className = 'download-notification__content';

    const messageSpan = document.createElement('span');
    messageSpan.className = 'download-notification__message';
    messageSpan.textContent = message;

    const closeButton = document.createElement('button');
    closeButton.className = 'download-notification__close';
    closeButton.type = 'button';
    closeButton.textContent = '×';
    closeButton.addEventListener('click', () => {
      notification.remove();
    });

    content.appendChild(messageSpan);
    content.appendChild(closeButton);
    notification.appendChild(content);

    if (!document.getElementById('download-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'download-notification-styles';
      styles.textContent = `
        .download-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
        }

        .download-notification__content {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 300px;
          max-width: 500px;
        }

        .download-notification--success .download-notification__content {
          background: #d4edda;
          color: #155724;
          border-left: 4px solid #28a745;
        }

        .download-notification--error .download-notification__content {
          background: #f8d7da;
          color: #721c24;
          border-left: 4px solid #dc3545;
        }

        .download-notification--warning .download-notification__content {
          background: #fff3cd;
          color: #856404;
          border-left: 4px solid #ffc107;
        }

        .download-notification--info .download-notification__content {
          background: #d1ecf1;
          color: #0c5460;
          border-left: 4px solid #17a2b8;
        }

        .download-notification__message {
          flex: 1;
          font-size: 14px;
        }

        .download-notification__close {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.2s;
        }

        .download-notification__close:hover {
          opacity: 1;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  }

  /**
   * Check download eligibility (optional pre-check)
   */
  async function checkDownloadEligibility(productId) {
    if (!DOWNLOAD_API_ACTIVE || !window.v0Customer?.loggedIn) {
      return false;
    }

    try {
      const response = await fetch(`${DOWNLOAD_ENDPOINT}/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: productId
        })
      });

      const data = await response.json();
      return data.canDownload;
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      return false;
    }
  }

  // Initialize on load
  init();

  // Export for external use
  window.DownloadManager = {
    checkEligibility: checkDownloadEligibility,
    loadSubscriptionStatus: loadSubscriptionStatus,
    showNotification: showNotification,
    isEnabled: DOWNLOAD_API_ACTIVE
  };

})();

